import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import ResponsiveImage from "./templates/utils/ResponsiveImage";
import {
  getShowcaseItems,
  getShowcaseGroup,
  getImageSrc,
  formatPrice,
  QuotePartyMember,
} from "./templates/utils/showcase";
import { ShowcaseCTAs } from "./templates/ShowcaseCard";
import PlacesTemplate from "./templates/PlacesTemplate";
import PoiMapTemplate from "./templates/PoiMapTemplate";
import ItineraryTemplate from "./templates/ItineraryTemplate";
import VideosTemplate from "./templates/VideosTemplate";
import { useSliderState } from "./SliderState";
import BookingPanel from "./booking/BookingPanel";
import useBookingState, { useBookingEnabled } from "./booking/BookingState";
import { isBookableItem, startBookingFromShowcase } from "./booking/fromShowcase";
import localization from "../../../../assets/localization";

interface ImmersiveViewProps {
  language: string;
  chatPanel?: React.ReactNode;
  /**
   * Live viewport answer, threaded from Slider (which gets it from App's
   * resize listener). This used to read `sliderState.isMobile`, a snapshot
   * written only when a template arrives or the cache hydrates · so it went
   * stale on rotate or resize, and the booking layout was hanging off it.
   */
  isMobile?: boolean;
}

const peopleLine = (member: QuotePartyMember, copy: any) => {
  const parts = [
    member.adults
      ? `${member.adults} ${
          member.adults === 1 ? copy.adult : copy.adults
        }`
      : null,
    member.children
      ? `${member.children} ${
          member.children === 1 ? copy.child : copy.children
        }`
      : null,
  ].filter(Boolean);
  return parts.join(", ");
};

export default function ImmersiveView({
  language,
  chatPanel,
  isMobile: isMobileProp,
}: ImmersiveViewProps) {
  const sliderState = useSliderState();
  const t = localization.get(language);
  const reduceMotion = useReducedMotion();
  const isMobile = isMobileProp ?? sliderState.isMobile ?? false;

  const template = sliderState.currentTemplate;
  const isMap = template?.type === "map" || template?.type === "map_point";
  const isPoiMap = template?.type === "poi_map";
  const isItinerary = template?.type === "itinerary";
  const isVideo = template?.type === "videos";
  const items = useMemo(() => getShowcaseItems(template), [template]);
  const group = useMemo(() => getShowcaseGroup(template), [template]);
  const itemIndex = Math.max(
    0,
    Math.min(sliderState.subTemplateItemIndex ?? 0, items.length - 1)
  );
  const item = items[itemIndex];

  const booking = useBookingState();
  // Only a quote is buyable, and only once the capability is switched on.
  // Everything else keeps today's deep link.
  const canBook = useBookingEnabled() && isBookableItem(item);

  const [imageIndex, setImageIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const lastCenteredRail = useRef(-1);

  const startBooking = (rateIndex?: number) =>
    startBookingFromShowcase(item, template, rateIndex);

  useEffect(() => {
    setImageIndex(0);
  }, [itemIndex, template]);

  // Keeps the active rail tile in view across the AnimatePresence remounts
  // (same trick as the showcase chips: ref on the active element).
  const centerActiveRailItem = (tile: HTMLButtonElement | null) => {
    if (!tile || lastCenteredRail.current === itemIndex) return;
    const rail = tile.parentElement;
    if (!rail) return;
    lastCenteredRail.current = itemIndex;
    rail.scrollTo({
      left: Math.max(0, tile.offsetLeft - (rail.clientWidth - tile.offsetWidth) / 2),
      behavior: "smooth",
    });
  };

  const images = item?.images ?? [];

  // Like the docked card (ShowcaseCard since 2fe9cd8): what is on screen is a
  // photo, so the photo's KB-authored title takes the headline slot and its
  // description sits right under it. The fact's own title drops to the
  // context pill over the hero, which otherwise only carries a category word.
  const photoTitle = ((images[imageIndex]?.title as string) ?? "").trim();
  const photoDescription = ((images[imageIndex]?.description as string) ?? "").trim();
  const headline = photoTitle || item?.title;
  const categoryLabel = template?.type === "quote" ? t.explore_rooms : t.explore_discover;
  const hasNamedContext = !!photoTitle && !!item?.title;

  const nextImage = () =>
    setImageIndex((i) => (images.length ? (i + 1) % images.length : 0));
  const prevImage = () =>
    setImageIndex((i) =>
      images.length ? (i - 1 + images.length) % images.length : 0
    );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The chat stays alive next to the fullscreen view · don't hijack
      // arrows/Escape while the guest is typing a message.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable)
        return;
      if (e.key === "Escape") {
        // Escape backs out of the booking before it backs out of the view ·
        // otherwise one keystroke silently throws away a filled-in form.
        // Read the store rather than the closure: this effect only re-runs on
        // image count, so `booking` here would be stale.
        const draft = useBookingState.getState();
        if (draft.isActive) draft.cancel();
        else sliderState.setExpanded(false);
      }
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  if (!isMap && !isPoiMap && !isItinerary && !isVideo && !item) return null;

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta < 0) nextImage();
    else prevImage();
  };

  const renderShowcase = () => (
    <>
      <div
        className="immersive-gallery"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {images.map((image, index) => (
          <ResponsiveImage
            key={`${itemIndex}-${index}`}
            src={getImageSrc(image)}
            alt={image.description ?? item.title}
            className={`showcase-hero-img ${
              index === imageIndex ? "active" : ""
            }`}
          />
        ))}

        <div className="showcase-scrim-top" />

        {/* Where am I: the fact (or its category) + position within the template */}
        <div className={`showcase-context ${hasNamedContext ? "named" : ""}`}>
          <span className="showcase-context-label">
            {hasNamedContext ? item.title : categoryLabel}
          </span>
          {items.length > 1 && (
            <span className="showcase-context-count">
              {itemIndex + 1}/{items.length}
            </span>
          )}
        </div>

        {images.length > 1 && (
          <>
            <button
              className="showcase-nav-btn visible"
              style={{ left: "16px" }}
              onClick={prevImage}
              aria-label="Previous"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button
              className="showcase-nav-btn visible"
              style={{ right: "16px" }}
              onClick={nextImage}
              aria-label="Next"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>

            <div className="immersive-counter">
              {t.photo} {imageIndex + 1} / {images.length}
            </div>

            {/* Filmstrip */}
            <div className="immersive-filmstrip">
              {images.map((image, index) => (
                <button
                  key={index}
                  className={`immersive-thumb ${
                    index === imageIndex ? "active" : ""
                  }`}
                  onClick={() => setImageIndex(index)}
                >
                  <ResponsiveImage src={getImageSrc(image)} alt="" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Details under the images: text left, rates & booking right */}
      <div className="immersive-details">
        <div className="immersive-details-main">
          <div className="immersive-title">{headline}</div>
          {photoDescription && (
            <div className="immersive-photo-caption">{photoDescription}</div>
          )}
          {template.type === "quote" && (
            <div className="showcase-chip">{t.available}</div>
          )}
          <div className="immersive-desc">
            {item.isMarkdown ? (
              <ReactMarkdown>{item.description ?? ""}</ReactMarkdown>
            ) : (
              item.description
            )}
          </div>
        </div>

        {/* The side panel only exists to carry rates or CTAs: a fact with
            neither gets no box at all, not an empty tinted rounded panel. */}
        {(item.rates || item.bookUrl || item.ctas || group) && (
          <div className="immersive-details-side">
          {group && (
            <motion.div
              className="immersive-group"
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.26, ease: "easeOut" }}
            >
              <div className="immersive-rail-label">{t.quote_group.title}</div>
              <div className="immersive-group-rooms">
                {group.members.map((member, index) => {
                  const composition = peopleLine(member, t.quote_group);
                  return (
                    <div
                      key={`${member.room ?? index}-${index}`}
                      className="immersive-group-row"
                    >
                      <span className="immersive-group-room">
                        <span className="immersive-group-room-label">
                          {t.quote_group.room.replace(
                            "{n}",
                            String(member.room ?? index + 1)
                          )}
                        </span>
                        <span className="immersive-group-room-name">
                          {[member.title, composition]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      <span className="immersive-group-price">
                        {formatPrice(member.price)}
                      </span>
                    </div>
                  );
                })}
              </div>
              {group.total !== null && (
                <div className="immersive-group-total">
                  <span>{t.quote_group.total}</span>
                  <strong>{formatPrice(group.total)}</strong>
                </div>
              )}
              {/* What the total buys: without the rate and its terms the
                  figure says nothing about breakfast or cancellation. */}
              {group.total !== null && group.options[0] && (
                <div className="immersive-group-option immersive-group-chosen">
                  <span className="immersive-group-option-label">
                    {group.options[0].label}
                  </span>
                  {group.options[0].conditions && (
                    <span className="immersive-group-option-conditions">
                      {group.options[0].conditions}
                    </span>
                  )}
                </div>
              )}
              {group.options.length > 1 && (
                <div className="immersive-group-alternatives">
                  <div className="immersive-group-alt-label">
                    {t.quote_group.other_options}
                  </div>
                  {group.options.slice(1).map((option, index) => (
                    <div
                      key={`${option.label}-${index}`}
                      className="immersive-group-option"
                    >
                      <div className="immersive-group-option-row">
                        <span className="immersive-group-option-label">
                          {option.label}
                        </span>
                        <span className="immersive-group-option-price">
                          {formatPrice(option.total)}
                        </span>
                      </div>
                      {option.conditions && (
                        <span className="immersive-group-option-conditions">
                          {option.conditions}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
          {item.rates && (
            <div className="immersive-rates">
              <div className="immersive-rail-label">{t.rates}</div>
              {item.rates.map((rate, index) =>
                canBook ? (
                  <button
                    key={index}
                    className="immersive-rate-row selectable"
                    onClick={() => startBooking(index)}
                  >
                    <span className="immersive-rate-main">
                      <span className="immersive-rate-title">{rate.title}</span>
                      {rate.conditions && (
                        <span className="immersive-rate-conditions">
                          {rate.conditions}
                        </span>
                      )}
                    </span>
                    <span className="immersive-rate-price">
                      {formatPrice(rate.price)}
                    </span>
                    <i className="fa-solid fa-chevron-right immersive-rate-go"></i>
                  </button>
                ) : (
                  <div key={index} className="immersive-rate-row">
                    <span className="immersive-rate-main">
                      <span className="immersive-rate-title">{rate.title}</span>
                      {rate.conditions && (
                        <span className="immersive-rate-conditions">
                          {rate.conditions}
                        </span>
                      )}
                    </span>
                    <span className="immersive-rate-price">
                      {formatPrice(rate.price)}
                    </span>
                  </div>
                )
              )}
            </div>
          )}
          <div className="showcase-actions">
            {canBook ? (
              <button className="showcase-cta" onClick={() => startBooking()}>
                {t.book_now}
              </button>
            ) : (
              <ShowcaseCTAs item={item} language={language} />
            )}
          </div>
          </div>
        )}

        {/* Sibling items rail, full width */}
        {items.length > 1 && (
          <div className="immersive-rail-section">
            <div className="immersive-rail-label">{t.explore}</div>
            <div className="immersive-rail">
              {items.map((railItem, index) => (
                <button
                  key={index}
                  ref={index === itemIndex ? centerActiveRailItem : undefined}
                  className={`immersive-rail-item ${
                    index === itemIndex ? "active" : ""
                  }`}
                  onClick={() => sliderState.setSubTemplateItemIndex(index)}
                >
                  {railItem.images?.[0] && (
                    <ResponsiveImage
                      src={getImageSrc(railItem.images[0])}
                      alt=""
                    />
                  )}
                  <span>{railItem.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );

  // Same soft-rise language as the slider panel: content swaps (anchor
  // clicks, rail jumps, item chips) never snap.
  const contentKey = booking.isActive
    ? `booking-${booking.draft?.id ?? ""}`
    : isMap || isVideo
    ? `${template?.type}-${template?.__key ?? ""}`
    : `${template?.__key ?? template?.type}-${itemIndex}`;

  return (
    <>
      <motion.div
        className="immersive-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        // A stray click outside must not discard a half-filled booking. Once
        // it's confirmed there's nothing left to lose, so the backdrop works
        // again.
        onClick={() => {
          if (booking.isActive && booking.step !== "success") return;
          sliderState.setExpanded(false);
        }}
      />

      <motion.div
        className={`immersive-container ${isMobile ? "mobile" : ""} ${
          chatPanel ? "with-chat" : ""
        } ${isMap || isPoiMap || isItinerary ? "map-view" : ""}`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
      >
        {/* Content zone: full-width gallery with the details below it */}
        <div className="immersive-main">
          {/* Without the chat column (mobile) the close lives on the content */}
          {/* The booking panel carries its own cancel, and on mobile the two
              buttons land on top of each other · and mean different things. */}
          {!chatPanel && !booking.isActive && (
            <button
              className="immersive-close"
              onClick={() => sliderState.setExpanded(false)}
              aria-label={t.back_to_chat}
            >
              <i className="fa-solid fa-x"></i>
            </button>
          )}

          <AnimatePresence mode="sync">
            <motion.div
              key={contentKey}
              className="immersive-content"
              initial={{ opacity: 0, scale: 1.025 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.25 } }}
              transition={{ duration: 0.45, ease: [0.3, 0.7, 0.3, 1] }}
            >
              {booking.isActive ? (
                <BookingPanel language={language} isMobile={isMobile} />
              ) : isMap ? (
                <PlacesTemplate template={template} language={language} />
              ) : isPoiMap ? (
                <PoiMapTemplate template={template} language={language} />
              ) : isItinerary ? (
                <ItineraryTemplate template={template} language={language} />
              ) : isVideo ? (
                <VideosTemplate template={template} language={language} />
              ) : (
                renderShowcase()
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* The conversation, native to the fullscreen surface */}
        {chatPanel && <div className="immersive-chat-col">{chatPanel}</div>}
      </motion.div>
    </>
  );
}
